import { AxiosProgressEvent, CancelTokenSource } from 'axios';

import { env } from 'config';

import { EntityModules } from 'core/constants';

import BaseAPI from 'utils/api/base';

import {
  ActivityListResponse as VatixActivityListResponse,
  MessageActivityResponse,
  OpenRequest,
  ActivityResponse as VatixActivityResponse,
  FileActivityResponse,
} from 'ui/types';

import {
  QueryParams,
  PaginationParams,
  ActivityResponse,
  APIRequest,
  UuidableName,
  SearchResponse,
  PaginatedResponse,
  SearchRequest,
} from 'utils/api/types';

import { UserSearchResponse } from 'utils/api/modules/user/user.types';

import {
  EntitySubmissionDraftType,
  PublicSubmissionUserDetails,
} from 'stores/EntitiesFormSubmission/EntitiesFormSubmission';

import { PipelineDetailsType } from 'stores/PipelineDetails/PipelineDetails';

import { IncidentType, AnswerTypes } from '../incidents/incidents.types';

import {
  ShareDocumentLinkResponse,
  AssessmentResponse,
  AssignmentRuleResponseType,
  AssignmentRuleRequestType,
  AssignmentRulePipelineResponseType,
  AssignmentRulePipelineRequestType,
  DefaultDocumentAccess,
  DocumentOwnerResponse,
  DocumentPermissionRequest,
  DocumentPermissionResponse,
  DocumentTokenResponse,
  EntityDetailsType,
  EntityFieldType,
  EntityLayoutResponse,
  EntityLayoutType,
  EntityCountType,
  EntityFormType,
  EntityFormUpdateRequest,
  PublicFormsLinkResponse,
  FormToFieldMappingResponse,
  EntityHistoryResponseType,
  FoldersResponse,
  FolderType,
  AcknowledgementRequestCreateResponse,
  QuestionActivityHost,
  QuestionActivityStatsResponse,
  QuestionLink,
  QuestionTaskLink,
  AcknowledgementRequestDetail,
  AcknowledgementRequestSummary,
  AcknowledgementRow,
  CreateAcknowledgementRequestBody,
  PreviewRecipientsBody,
  PreviewRecipientsResponse,
  PreviewAssignmentRuleTargetGroupsBody,
  PreviewAssignmentRuleTargetGroupsResponse,
  PreviewDocumentPermissionLicenceCoverageBody,
  PreviewDocumentPermissionLicenceCoverageResponse,
  ListAcknowledgementRequestsParams,
  ListAcknowledgementsParams,
  ListMyAcknowledgementsParams,
  MyAcknowledgementRow,
  SetDocumentTiptapVersionResponse,
  AcknowledgementOpenResponse,
  AcknowledgementReceiptResponse,
  SubmitAcknowledgementBody,
  SubmissionVersionType,
  LibraryArchiveResponse,
  LibraryCategory,
  LibraryCategoryList,
  LibraryEntityType,
  RiskControlOption,
  RiskControlOptionsParams,
  RiskControlOptionsResponse,
} from './entities.types';

class EntitiesAPI extends BaseAPI {
  // collaborators
  updateEntityCollaborators = (
    entityType: string,
    uuid: string,
    data: { uuid: string }
  ): APIRequest<UuidableName[]> => {
    const url = `/entities/${entityType}/${uuid}/collaborators/`;
    const method = 'PATCH';

    return this.request<UuidableName[]>({
      url,
      method,
      data,
    });
  };

  deleteEntityCollaborator = (entityType: string, uuid: string, data: { uuid: string }): APIRequest<UuidableName[]> => {
    const url = `/entities/${entityType}/${uuid}/collaborators/${data.uuid}/`;
    const method = 'DELETE';

    return this.request<UuidableName[]>({
      url,
      method,
    });
  };

  // assessment
  loadEntitiesAssessments = (entityType: string, uuid: string): APIRequest<AssessmentResponse[]> => {
    const url = `/entities/${entityType}/${uuid}/assessments/`;
    const method = 'GET';

    return this.request<AssessmentResponse[]>({
      url,
      method,
    });
  };

  addEntitiesAssessments = (
    entityType: string,
    uuid: string,
    data: Partial<AssessmentResponse>
  ): APIRequest<AssessmentResponse[]> => {
    const url = `/entities/${entityType}/${uuid}/assessments/`;
    const method = 'POST';

    return this.request<AssessmentResponse[]>({
      url,
      method,
      data,
    });
  };

  deleteEntitiesAssessments = (entityType: string, uuid: string, assessmentId: string): APIRequest<void> => {
    const url = `/entities/${entityType}/${uuid}/assessments/${assessmentId}/`;
    const method = 'DELETE';

    return this.request<void>({
      url,
      method,
    });
  };

  editEntitiesAssessments = (
    entityType: string,
    uuid: string,
    assessmentId: string,
    data: Partial<AssessmentResponse>
  ): APIRequest<AssessmentResponse> => {
    const url = `/entities/${entityType}/${uuid}/assessments/${assessmentId}/`;
    const method = 'PATCH';

    return this.request<AssessmentResponse>({
      url,
      method,
      data,
    });
  };

  // entities
  loadEntitiesDetails = <T = EntityDetailsType>(entityType: string, uuid: string): APIRequest<T> => {
    const url = `/entities/${entityType}/${uuid}/`;
    const method = 'GET';

    return this.request<T>({
      url,
      method,
    });
  };

  createEntity = (
    entityType: string,
    entityFieldValues: { [key: string]: string | number | string[] | null },
    folderUuid?: string | null,
    scopeUnit?: string | null
  ): APIRequest<EntityDetailsType> => {
    const url = `/entities/${entityType}/`;
    const method = 'POST';

    return this.request<EntityDetailsType>({
      url,
      method,
      data: { entityType, entityFieldValues, folderUuid, ...(scopeUnit ? { scopeUnit } : {}) },
    });
  };

  archiveEntity = (entityType: LibraryEntityType, uuid: string): APIRequest<LibraryArchiveResponse> =>
    this.request<LibraryArchiveResponse>({ url: `/entities/${entityType}/${uuid}/archive/`, method: 'POST' });

  unarchiveEntity = (entityType: LibraryEntityType, uuid: string): APIRequest<LibraryArchiveResponse> =>
    this.request<LibraryArchiveResponse>({ url: `/entities/${entityType}/${uuid}/unarchive/`, method: 'POST' });

  loadCategories = (
    entityType: LibraryEntityType,
    cancelTokenSource?: CancelTokenSource
  ): APIRequest<LibraryCategoryList> =>
    this.request<LibraryCategoryList>({
      url: `/entities/${entityType}/categories/`,
      method: 'GET',
      cancelTokenSource,
    });

  createCategory = (entityType: LibraryEntityType, name: string): APIRequest<LibraryCategory> =>
    this.request<LibraryCategory>({ url: `/entities/${entityType}/categories/`, method: 'POST', data: { name } });

  renameCategory = (entityType: LibraryEntityType, key: string, name: string): APIRequest<LibraryCategory> =>
    this.request<LibraryCategory>({
      url: `/entities/${entityType}/categories/${key}/`,
      method: 'PATCH',
      data: { name },
    });

  deleteCategory = (entityType: LibraryEntityType, key: string): APIRequest<void> =>
    this.request<void>({ url: `/entities/${entityType}/categories/${key}/`, method: 'DELETE' });

  addRiskControl = (riskUuid: string, controlUuid: string): APIRequest<void> =>
    this.request<void>({
      url: `/entities/risks/${riskUuid}/linked-controls/`,
      method: 'POST',
      data: { control: controlUuid },
    });

  removeRiskControl = (riskUuid: string, controlUuid: string): APIRequest<void> =>
    this.request<void>({ url: `/entities/risks/${riskUuid}/linked-controls/${controlUuid}/`, method: 'DELETE' });

  loadRiskControlOptions = (
    riskUuid: string,
    params: RiskControlOptionsParams
  ): APIRequest<RiskControlOptionsResponse> =>
    this.request<RiskControlOptionsResponse>({
      url: `/entities/risks/${riskUuid}/control-options/`,
      method: 'GET',
      params,
    });

  searchRiskControlOptions =
    (riskUuid: string) =>
    (params?: SearchRequest): APIRequest<RiskControlOption[]> => {
      const page = this.loadRiskControlOptions(riskUuid, {
        search: params?.query,
        limit: params?.limit,
        offset: params?.offset,
      });
      const call = async () => {
        const response = await page();
        return { ...response, data: response.data.results };
      };
      call.config = page.config;
      return call;
    };

  deleteEntity = (entityType: string, uuid: string): APIRequest<void> => {
    const url = `/entities/${entityType}/${uuid}/`;
    const method = 'DELETE';

    return this.request<void>({
      url,
      method,
    });
  };

  editEntity = <T = EntityDetailsType>(
    entityType: string,
    uuid: string,
    data: Record<string, string | number | null | {}>,
    folderUuid?: string | null
  ): APIRequest<T> => {
    const url = `/entities/${entityType}/${uuid}/`;
    const method = 'PATCH';

    return this.request<T>({
      url,
      method,
      data: { entityFieldValues: data, folderUuid },
    });
  };

  setSiteUnit = <T = EntityDetailsType>(uuid: string, scopeUnit: string | null): APIRequest<T> => {
    const url = `/entities/${EntityModules.Sites}/${uuid}/`;
    const method = 'PATCH';

    return this.request<T>({
      url,
      method,
      data: { scopeUnit },
    });
  };

  createEntityField = (entityType: string, data: Partial<EntityFieldType>): APIRequest<EntityFieldType> => {
    const url = `/entities/${entityType}/fields/`;
    const method = 'POST';

    return this.request<EntityFieldType>({
      url,
      method,
      data,
    });
  };

  editEntityField = (
    entityType: string,
    fieldId: string,
    data: Partial<EntityFieldType>
  ): APIRequest<EntityFieldType> => {
    const url = `/entities/${entityType}/fields/${fieldId}/`;
    const method = 'PATCH';

    return this.request<EntityFieldType>({
      url,
      method,
      data,
    });
  };

  deleteEntityField = (fieldType: string, fieldId: string): APIRequest<void> => {
    const url = `/entities/${fieldType}/fields/${fieldId}/`;
    const method = 'DELETE';

    return this.request<void>({
      url,
      method,
    });
  };

  loadEntityFields = (
    entityType: string,
    params: Partial<PaginationParams> & { pagination?: string; protector_type?: string; include_deleted?: boolean }
  ): APIRequest<PaginatedResponse<EntityFieldType>> => {
    const searchParams = new URLSearchParams();

    if (params.pagination) {
      searchParams.set('pagination', params.pagination);
    }
    if (params.limit !== undefined) {
      searchParams.set('limit', String(params.limit));
    }
    if (params.offset !== undefined) {
      searchParams.set('offset', String(params.offset));
    }
    if (params.protector_type) {
      searchParams.set('protector_type', params.protector_type);
    }
    if (params.include_deleted) {
      searchParams.set('include_deleted', String(params.include_deleted));
    }

    return this.request<PaginatedResponse<EntityFieldType>>({
      url: `/entities/${entityType}/fields/?${searchParams.toString()}`,
      method: 'GET',
    });
  };

  loadEntityLayout = (entityType: string): APIRequest<EntityLayoutResponse> => {
    const url = `/entities/${entityType}/layout/`;
    const method = 'GET';

    return this.request<EntityLayoutResponse>({
      url,
      method,
    });
  };

  updateEntityLayout = (
    entityType: string,
    data: EntityLayoutType
  ): APIRequest<{ layout: EntityLayoutType; createdAt: string }> => {
    const url = `/entities/${entityType}/layout/`;
    const method = 'POST';

    return this.request<{ layout: EntityLayoutType; createdAt: string }>({
      url,
      method,
      data: { layout: data },
    });
  };

  searchForms = (
    entityType: string,
    params?: Partial<{
      query: string;
      limit: number;
      offset: number;
      audience?: string;
      ordering?: string;
      uuid?: string;
    }>
  ): APIRequest<UuidableName[]> => {
    const url = `/entities/${entityType}/forms/search/`;
    const method = 'GET';

    return this.request<UuidableName[]>({
      url,
      method,
      params,
    });
  };

  uploadEntityFormImage = (entityType: string, image: File): APIRequest<{ file: string; uuid: string }> => {
    const url = `/entities/${entityType}/forms/files/`;
    const method = 'POST';

    const data = new FormData();
    data.append('file', image);

    return this.request<{ file: string; uuid: string }>({
      url,
      method,
      data,
    });
  };

  deleteEntityFormImage = (entityType: string, fileId: string): APIRequest<void> => {
    const url = `/entities/${entityType}/forms/files/${fileId}/`;
    const method = 'DELETE';

    return this.request<void>({
      url,
      method,
    });
  };

  loadEntityForm = (entityType: string, formId: string): APIRequest<EntityFormType> => {
    const url = `/entities/${entityType}/forms/${formId}/`;
    const method = 'GET';

    return this.request<EntityFormType>({
      url,
      method,
    });
  };

  exportEntity = (entityType: string, uuid: string, params: QueryParams): OpenRequest => {
    const url = `/api/entities/${entityType}/${uuid}/export/pdf/`;

    return this.open({
      url,
      params,
    });
  };

  loadFormToFieldMapping = (entityType: string, formId: string): APIRequest<FormToFieldMappingResponse> => {
    const url = `/entities/${entityType}/forms/${formId}/rules/`;
    const method = 'GET';

    return this.request<FormToFieldMappingResponse>({
      url,
      method,
    });
  };

  saveFormToFieldMapping = (entityType: string, formId: string, data: FormToFieldMappingResponse): APIRequest<void> => {
    const url = `/entities/${entityType}/forms/${formId}/rules/`;
    const method = 'PUT';

    return this.request<void>({
      url,
      method,
      data,
    });
  };

  createEntityForm = (entityType: string, data: Partial<EntityFormType>): APIRequest<EntityFormType> => {
    const url = `/entities/${entityType}/forms/`;
    const method = 'POST';

    return this.request<EntityFormType>({
      url,
      method,
      data,
    });
  };

  getEntityForms = (entityType: string, params: QueryParams): APIRequest<PaginatedResponse<EntityFormType>> => {
    const url = `/entities/${entityType}/forms/`;
    const method = 'GET';

    return this.request<PaginatedResponse<EntityFormType>>({
      url,
      method,
      params,
    });
  };

  duplicateEntityForm = (entityType: string, formId: string): APIRequest<EntityFormType> => {
    const url = `/entities/${entityType}/forms/${formId}/duplicate/`;
    const method = 'POST';

    return this.request<EntityFormType>({
      url,
      method,
    });
  };

  loadPublicFormsLink = (
    formId: string,
    cancelTokenSource?: CancelTokenSource
  ): APIRequest<PublicFormsLinkResponse> => {
    const url = `/entities/events/forms/${formId}/public-forms-link/`;
    const method = 'GET';

    return this.request<PublicFormsLinkResponse>({
      url,
      method,
      cancelTokenSource,
    });
  };

  getEntityFormsDrafts = (entityType: string, params?: QueryParams): APIRequest<PaginatedResponse<EntityFormType>> => {
    const url = `/entities/${entityType}/drafts/`;
    const method = 'GET';

    return this.request<PaginatedResponse<EntityFormType>>({
      url,
      method,
      params,
    });
  };

  /** `relatedEntity` relates the new audit to that record as a whole (no question). Audits only. */
  createEntityFormsDrafts = (entityType: string, form: string, relatedEntity?: string): APIRequest<EntityFormType> => {
    const url = `/entities/${entityType}/drafts/`;
    const method = 'POST';

    return this.request<EntityFormType>({
      url,
      method,
      data: { form, ...(relatedEntity ? { relatedEntity } : {}) },
    });
  };

  /** Binds a draft to an audit record that already exists. `201` on create, `200` with the existing
   * draft if one appeared first. Mutually exclusive with `form`. */
  bindEntityFormsDraft = (entityType: string, entity: string): APIRequest<{ uuid: string }> => {
    const url = `/entities/${entityType}/drafts/`;
    const method = 'POST';

    return this.request<{ uuid: string }>({
      url,
      method,
      data: { entity },
    });
  };

  getPublicSubmission = (
    entityType: string,
    publicUuid: string,
    publicFormSessionId: string
  ): APIRequest<EntitySubmissionDraftType> => {
    const url = `/entities/${entityType}/public/${publicUuid}/`;
    const method = 'GET';

    return this.request<EntitySubmissionDraftType>({
      url,
      method,
      params: { publicFormSessionId },
    });
  };

  uploadEntitiesPublicReporterActivitiesFile = (
    entityType: string,
    entityId: string,
    file: File,
    onUploadProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): APIRequest<ActivityResponse> => {
    const method = 'POST';

    const url = `/entities/${entityType}/${entityId}/submission/files/`;

    const data = new FormData();
    data.append('file', file);

    return this.request<ActivityResponse>({
      url,
      method,
      data,
      onUploadProgress,
      cancelTokenSource,
    });
  };

  addEntitiesPublicReporterActivitiesMessage = (
    entityType: string,
    entityId: string,
    message: string,
    taggedUsers?: UserSearchResponse[]
  ): APIRequest<MessageActivityResponse> => {
    const method = 'POST';

    const url = `/entities/${entityType}/${entityId}/submission/comments/`;

    return this.request<MessageActivityResponse>({
      url,
      method,
      data: { message, taggedUsers },
    });
  };

  getDraft = (entityType: string, draftId: string): APIRequest<EntitySubmissionDraftType> => {
    const url = `/entities/${entityType}/drafts/${draftId}/`;
    const method = 'GET';

    return this.request<EntitySubmissionDraftType>({
      url,
      method,
    });
  };

  getPreview = (
    entityType: string,
    isPublicSubmission: boolean,
    uuid: string,
    data?: {
      content: Record<string, AnswerTypes>;
      stack: string[];
      publicSubmissionDetails?: PublicSubmissionUserDetails;
      publicSubmissionUuid?: string;
      publicFormSessionId?: string;
    }
  ): APIRequest<EntitySubmissionDraftType> => {
    const url = `/entities/${entityType}/${isPublicSubmission ? 'public' : 'drafts'}/${uuid}/submit/?preview`;
    const method = 'POST';

    return this.request<EntitySubmissionDraftType>({
      url,
      method,
      data,
    });
  };

  getNextSection = (
    entityType: string,
    isPublicSubmission: boolean,
    uuid: string,
    data: {
      stack: string[];
      content?: Record<string, AnswerTypes>;
      publicSubmissionDetails?: PublicSubmissionUserDetails;
      publicFormSessionId?: string;
    }
  ): APIRequest<EntitySubmissionDraftType> => {
    const url = `/entities/${entityType}/${isPublicSubmission ? 'public' : 'drafts'}/${uuid}/?next`;
    const method = 'PATCH';

    return this.request<EntitySubmissionDraftType>({
      url,
      method,
      data,
    });
  };

  getPreviousSection = (
    entityType: string,
    isPublicSubmission: boolean,
    uuid: string,
    data: {
      stack: string[];
      content?: Record<string, AnswerTypes>;
      publicSubmissionDetails?: PublicSubmissionUserDetails;
      publicFormSessionId?: string;
    }
  ): APIRequest<EntitySubmissionDraftType> => {
    const url = `/entities/${entityType}/${isPublicSubmission ? 'public' : 'drafts'}/${uuid}/`;
    const method = 'PATCH';

    return this.request<EntitySubmissionDraftType>({
      url,
      method,
      data,
    });
  };

  updateEntityFormAttachment = (
    entityType: string,
    isPublicSubmission: boolean,
    uuid: string,
    filename: string
  ): APIRequest<EntitySubmissionDraftType> => {
    const url = `/entities/${entityType}/${isPublicSubmission ? 'public' : 'drafts'}/${uuid}/`;
    const method = 'PATCH';

    return this.request<EntitySubmissionDraftType>({
      url,
      method,
      headers: {
        'Content-Type': 'multipart/form-data',
        'Content-Disposition': `attachment; filename=${filename}`,
      },
    });
  };

  updateEntityFormAutosave = (
    entityType: string,
    isPublicSubmission: boolean,
    uuid: string,
    data: {
      content: Record<string, AnswerTypes>;
      stack: string[];
      publicSubmissionDetails?: PublicSubmissionUserDetails;
      publicFormSessionId?: string;
    },
    files?: File[]
  ): APIRequest<EntitySubmissionDraftType> => {
    const url = `/entities/${entityType}/${isPublicSubmission ? 'public' : 'drafts'}/${uuid}/`;
    const method = 'PATCH';

    // If files are provided, construct multipart/form-data request
    if (files && files.length > 0) {
      const formData = new FormData();

      // Add the JSON body as the first part (as a string, not a Blob)
      formData.append('body', JSON.stringify(data));

      // Add each file
      files.forEach((file) => {
        formData.append(file.name, file, file.name);
      });

      return this.request<EntitySubmissionDraftType>({
        url,
        method,
        data: formData,
      });
    }

    // Regular JSON request if no files
    return this.request<EntitySubmissionDraftType>({
      url,
      method,
      data,
    });
  };

  submitEntityForm = (
    entityType: string,
    isPublicSubmission: boolean,
    uuid: string,
    data?: {
      content: Record<string, AnswerTypes>;
      stack: string[];
      publicSubmissionDetails?: PublicSubmissionUserDetails;
      publicSubmissionUuid?: string;
      publicFormSessionId?: string;
    },
    files?: File[]
  ): APIRequest<EntitySubmissionDraftType> => {
    const url = `/entities/${entityType}/${isPublicSubmission ? 'public' : 'drafts'}/${uuid}/submit/`;
    const method = 'POST';

    // If files are provided, construct multipart/form-data request
    if (files && files.length > 0) {
      const formData = new FormData();

      // Add the JSON body as the first part (as a string, not a Blob)
      formData.append('body', JSON.stringify(data));

      // Add each file
      files.forEach((file) => {
        formData.append(file.name, file, file.name);
      });

      return this.request<EntitySubmissionDraftType>({
        url,
        method,
        data: formData,
      });
    }

    return this.request<EntitySubmissionDraftType>({
      url,
      method,
      data,
    });
  };

  updateEntityForm = (
    entityType: string,
    formId: string,
    data: EntityFormUpdateRequest
  ): APIRequest<EntityFormType> => {
    const url = `/entities/${entityType}/forms/${formId}/`;
    const method = 'PATCH';

    return this.request<EntityFormType>({
      url,
      method,
      data,
    });
  };

  loadEntitiesCount = (entityType: string): APIRequest<EntityCountType> => {
    const url = `/entities/${entityType}/count/`;
    const method = 'GET';

    return this.request<EntityCountType>({
      url,
      method,
    });
  };

  loadEntitySubmissionActivities = (
    entityType: string,
    entityId: string,
    params: PaginationParams
  ): APIRequest<VatixActivityListResponse> => {
    const method = 'GET';
    const url = `/entities/${entityType}/${entityId}/submission/activities/`;

    return this.request<VatixActivityListResponse>({
      url,
      method,
      params,
    });
  };

  loadDraftActivities = (
    entityType: string,
    draftId: string,
    params: PaginationParams
  ): APIRequest<VatixActivityListResponse> => {
    const method = 'GET';
    const url = `/entities/${entityType}/drafts/${draftId}/activities/`;

    return this.request<VatixActivityListResponse>({
      url,
      method,
      params,
    });
  };

  addEntitySubmissionMessage = (
    entityType: string,
    entityId: string,
    message: string,
    taggedUsers?: UserSearchResponse[]
  ): APIRequest<MessageActivityResponse> => {
    const method = 'POST';
    const url = `/entities/${entityType}/${entityId}/submission/comments/`;

    return this.request<MessageActivityResponse>({
      url,
      method,
      data: { message, taggedUsers },
    });
  };

  addDraftMessage = (
    entityType: string,
    draftId: string,
    message: string,
    taggedUsers?: UserSearchResponse[]
  ): APIRequest<MessageActivityResponse> => {
    const method = 'POST';
    const url = `/entities/${entityType}/drafts/${draftId}/comments/`;

    return this.request<MessageActivityResponse>({
      url,
      method,
      data: { message, taggedUsers },
    });
  };

  uploadEntitySubmissionFile = (
    entityType: string,
    entityId: string,
    file: File,
    onUploadProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): APIRequest<FileActivityResponse> => {
    const method = 'POST';
    const url = `/entities/${entityType}/${entityId}/submission/files/`;

    const data = new FormData();
    data.append('file', file);

    return this.request<FileActivityResponse>({
      url,
      method,
      data,
      onUploadProgress,
      cancelTokenSource,
    });
  };

  loadDraftFiles = (entityType: string, draftId: string): APIRequest<FileActivityResponse[]> => {
    const method = 'GET';
    const url = `/entities/${entityType}/drafts/${draftId}/files/`;

    return this.request<FileActivityResponse[]>({
      url,
      method,
    });
  };

  uploadDraftFile = (
    entityType: string,
    draftId: string,
    file: File,
    onUploadProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): APIRequest<FileActivityResponse> => {
    const method = 'POST';
    const url = `/entities/${entityType}/drafts/${draftId}/files/`;

    const data = new FormData();
    data.append('file', file);

    return this.request<FileActivityResponse>({
      url,
      method,
      data,
      onUploadProgress,
      cancelTokenSource,
    });
  };

  removeEntitySubmissionMessage = (
    entityType: string,
    entityId: string,
    messageId: string
  ): APIRequest<MessageActivityResponse> => {
    const url = `/entities/${entityType}/${entityId}/submission/comments/${messageId}/`;
    const method = 'DELETE';

    return this.request<MessageActivityResponse>({
      url,
      method,
    });
  };

  removeDraftMessage = (
    entityType: string,
    draftId: string,
    messageId: string
  ): APIRequest<MessageActivityResponse> => {
    const url = `/entities/${entityType}/drafts/${draftId}/comments/${messageId}/`;
    const method = 'DELETE';

    return this.request<MessageActivityResponse>({
      url,
      method,
    });
  };

  removeEntitySubmissionFile = (entityType: string, entityId: string, fileId: string): APIRequest<void> => {
    const method = 'DELETE';
    const url = `/entities/${entityType}/${entityId}/submission/files/${fileId}/`;

    return this.request<void>({
      url,
      method,
    });
  };

  removeDraftFile = (entityType: string, draftId: string, fileId: string): APIRequest<void> => {
    const method = 'DELETE';
    const url = `/entities/${entityType}/drafts/${draftId}/files/${fileId}/`;

    return this.request<void>({
      url,
      method,
    });
  };

  loadEntityActivities = (
    entityType: string,
    entityId: string,
    params: PaginationParams
  ): APIRequest<VatixActivityListResponse> => {
    const method = 'GET';
    const url = `/entities/${entityType}/${entityId}/activities/`;

    return this.request<VatixActivityListResponse>({
      url,
      method,
      params,
    });
  };

  loadEntityAttachments = (
    entityType: string,
    entityId: string,
    params: PaginationParams
  ): APIRequest<PaginatedResponse<FileActivityResponse>> => {
    const method = 'GET';
    const url = `/entities/${entityType}/${entityId}/activities/?type=file`;

    return this.request<PaginatedResponse<FileActivityResponse>>({
      url,
      method,
      params,
    });
  };

  loadEntitySubmissions = (entityType: string, entityId: string): APIRequest<FileActivityResponse[]> => {
    const method = 'GET';
    const url = `/entities/${entityType}/${entityId}/submission/files/`;

    return this.request<FileActivityResponse[]>({
      url,
      method,
    });
  };

  addEntityMessage = (
    entityType: string,
    entityId: string,
    message: string,
    taggedUsers?: UserSearchResponse[]
  ): APIRequest<MessageActivityResponse> => {
    const method = 'POST';
    const url = `/entities/${entityType}/${entityId}/comments/`;

    return this.request<MessageActivityResponse>({
      url,
      method,
      data: { message, taggedUsers },
    });
  };

  uploadEntityFile = (
    entityType: string,
    entityId: string,
    file: File,
    onUploadProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): APIRequest<VatixActivityResponse> => {
    const method = 'POST';
    const url = `/entities/${entityType}/${entityId}/files/`;

    const data = new FormData();
    data.append('file', file);

    return this.request<VatixActivityResponse>({
      url,
      method,
      data,
      onUploadProgress,
      cancelTokenSource,
    });
  };

  removeEntityFile = (entityType: string, entityId: string, fileId: string): APIRequest<void> => {
    const method = 'DELETE';
    const url = `/entities/${entityType}/${entityId}/files/${fileId}/`;

    return this.request<void>({
      url,
      method,
    });
  };

  removeEntityMessage = (entityType: string, entityId: string, messageId: string): APIRequest<void> => {
    const method = 'DELETE';
    const url = `/entities/${entityType}/${entityId}/comments/${messageId}/`;

    return this.request<void>({
      url,
      method,
    });
  };

  getAssignmentRules = (): APIRequest<AssignmentRuleResponseType[]> => {
    const url = '/entities/events/assignment-rules/';
    const method = 'GET';

    return this.request<AssignmentRuleResponseType[]>({
      url,
      method,
    });
  };

  updateAssignmentRule = (data: AssignmentRuleRequestType[]): APIRequest<AssignmentRuleResponseType[]> => {
    const url = '/entities/events/assignment-rules/';
    const method = 'PUT';

    return this.request<AssignmentRuleResponseType[]>({
      url,
      method,
      data,
    });
  };

  searchEntity = (
    entity: string,
    organisationId: string | undefined,
    params?: Partial<{
      query: string;
      exact_query: string;
      limit: number;
      offset: number;
      ordering?: string;
      type?: IncidentType;
      public_uuid?: string;
      published_only?: boolean;
      task_linkable_only?: boolean;
    }>
  ): APIRequest<SearchResponse[]> => {
    const url = organisationId ? `/entities/public/${organisationId}/${entity}/search/` : `/entities/${entity}/search/`;
    const method = 'GET';

    return this.request<SearchResponse[]>({
      url,
      method,
      params,
    });
  };

  // sharing document
  loadShareDocumentLink = (entityId: string): APIRequest<Array<ShareDocumentLinkResponse>> => {
    const url = `/entities/documents/${entityId}/share/`;
    const method = 'GET';

    return this.request<Array<ShareDocumentLinkResponse>>({
      url,
      method,
    });
  };

  shareDocumentLink = (entityId: string): APIRequest<ShareDocumentLinkResponse> => {
    const url = `/entities/documents/${entityId}/share/`;
    const method = 'POST';

    return this.request<ShareDocumentLinkResponse>({
      url,
      method,
    });
  };

  toggleShareDocumentLink = (
    entityId: string,
    uuid: string,
    isActive: boolean
  ): APIRequest<ShareDocumentLinkResponse> => {
    const url = `/entities/documents/${entityId}/share/${uuid}/`;
    const method = 'PATCH';

    return this.request<ShareDocumentLinkResponse>({
      url,
      method,
      data: { isActive },
    });
  };

  // shared document
  loadSharedDocument = (
    entityType: string,
    entityId: string,
    shareId: string
  ): APIRequest<{
    uuid: string;
    name: string;
    createdAt: string;
    createdBy: UuidableName;
    tiptapToken: string;
    organisation: string;
  }> => {
    const url = `/entities/${entityType}/${entityId}/share/${shareId}/`;
    const method = 'GET';

    return this.request<{
      uuid: string;
      name: string;
      createdAt: string;
      createdBy: UuidableName;
      tiptapToken: string;
      organisation: string;
    }>({
      url,
      method,
    });
  };

  // pipelines
  loadEntityPipelines = (entityType: string): APIRequest<PaginatedResponse<PipelineDetailsType>> => {
    const url = `/entities/${entityType}/pipelines/`;
    const method = 'GET';

    return this.request<PaginatedResponse<PipelineDetailsType>>({
      url,
      method,
    });
  };

  searchEntityPipelines = (params?: SearchRequest & { uuid?: string }): APIRequest<PipelineDetailsType[]> => {
    const url = `/entities/events/pipelines/search/`;
    const method = 'GET';

    return this.request<PipelineDetailsType[]>({
      url,
      method,
      params,
    });
  };

  loadEntityPipeline = (entityType: string, uuid: string): APIRequest<PipelineDetailsType> => {
    const url = `/entities/${entityType}/${uuid}/pipeline/`;
    const method = 'GET';

    return this.request<PipelineDetailsType>({
      url,
      method,
    });
  };

  updateEntityStage = (entityType: string, entityId: string, data: { uuid: string }): APIRequest<void> => {
    const url = `/entities/${entityType}/${entityId}/stage/`;
    const method = 'PATCH';

    return this.request<void>({
      url,
      method,
      data,
    });
  };

  updateEntityPipeline = (entityType: string, entityId: string, data: { uuid: string }): APIRequest<void> => {
    const url = `/entities/${entityType}/${entityId}/pipeline/`;
    const method = 'PATCH';

    return this.request<void>({
      url,
      method,
      data,
    });
  };

  loadPipelineDetails = (entityType: string, pipelineId: string): APIRequest<PipelineDetailsType> => {
    const url = `/entities/${entityType}/pipelines/${pipelineId}/`;
    const method = 'GET';

    return this.request<PipelineDetailsType>({
      url,
      method,
    });
  };

  updatePipeline = (
    entityType: string,
    pipelineId: string,
    data: Partial<PipelineDetailsType>
  ): APIRequest<PipelineDetailsType> => {
    const url = `/entities/${entityType}/pipelines/${pipelineId}/`;
    const method = 'PATCH';

    return this.request<PipelineDetailsType>({
      url,
      method,
      data,
    });
  };

  deletePipeline = (entityType: string, pipelineId: string): APIRequest<void> => {
    const url = `/entities/${entityType}/pipelines/${pipelineId}/`;
    const method = 'DELETE';

    return this.request<void>({
      url,
      method,
    });
  };

  createPipeline = (entityType: string, name: string, key: string): APIRequest<UuidableName[]> => {
    const url = `/entities/${entityType}/pipelines/`;
    const method = 'POST';

    return this.request<UuidableName[]>({
      url,
      method,
      data: { name, key },
    });
  };

  loadEntityHistory = (
    entityType: string,
    entityId: string,
    params: PaginationParams
  ): APIRequest<PaginatedResponse<EntityHistoryResponseType>> => {
    const url = `/entities/${entityType}/${entityId}/history/`;
    const method = 'GET';

    return this.request<PaginatedResponse<EntityHistoryResponseType>>({
      url,
      method,
      params,
    });
  };

  loadSubmissionVersions = (
    entityType: string,
    entityId: string,
    cancelTokenSource?: CancelTokenSource
  ): APIRequest<{ results: SubmissionVersionType[] }> => {
    const url = `/entities/${entityType}/${entityId}/submission/versions/`;
    const method = 'GET';

    return this.request<{ results: SubmissionVersionType[] }>({
      url,
      method,
      cancelTokenSource,
    });
  };

  loadPublicFormList = (publicUuid: string, params?: QueryParams): APIRequest<SearchResponse[]> => {
    const url = `/entities/events/public/organisation/${publicUuid}/`;
    const method = 'GET';

    return this.request<SearchResponse[]>({
      url,
      method,
      params,
    });
  };

  getAssignmentRulesPipelines(): APIRequest<AssignmentRulePipelineResponseType[]> {
    const url = '/entities/events/pipelines/assignment-rules/';
    const method = 'GET';

    return this.request<AssignmentRulePipelineResponseType[]>({
      url,
      method,
    });
  }

  updateAssignmentRulePipelines(
    data: AssignmentRulePipelineRequestType[]
  ): APIRequest<AssignmentRulePipelineResponseType[]> {
    const url = '/entities/events/pipelines/assignment-rules/';
    const method = 'PUT';

    return this.request<AssignmentRulePipelineResponseType[]>({
      url,
      method,
      data,
    });
  }

  // Document permissions

  loadDocumentToken = (entityType: string, entityId: string): APIRequest<DocumentTokenResponse> => {
    const url = `/entities/${entityType}/${entityId}/token/`;
    return this.request<DocumentTokenResponse>({ url, method: 'GET' });
  };

  loadDocumentPermissions = (entityType: string, entityId: string): APIRequest<DocumentPermissionResponse[]> => {
    const url = `/entities/${entityType}/${entityId}/permissions/`;
    return this.request<DocumentPermissionResponse[]>({ url, method: 'GET' });
  };

  syncDocumentPermissions = (
    entityType: string,
    entityId: string,
    data: DocumentPermissionRequest[]
  ): APIRequest<DocumentPermissionResponse[]> => {
    const url = `/entities/${entityType}/${entityId}/permissions/`;
    return this.request<DocumentPermissionResponse[]>({ url, method: 'PUT', data });
  };

  previewDocumentPermissionLicenceCoverage = (
    entityType: string,
    entityId: string,
    data: PreviewDocumentPermissionLicenceCoverageBody
  ): APIRequest<PreviewDocumentPermissionLicenceCoverageResponse> => {
    const url = `/entities/${entityType}/${entityId}/permissions/preview-licence-coverage/`;
    return this.request<PreviewDocumentPermissionLicenceCoverageResponse>({ url, method: 'POST', data });
  };

  updateDocumentAccess = (
    entityType: string,
    entityId: string,
    data: { defaultAccess: DefaultDocumentAccess }
  ): APIRequest<{ defaultAccess: DefaultDocumentAccess }> => {
    const url = `/entities/${entityType}/${entityId}/access/`;
    return this.request<{ defaultAccess: DefaultDocumentAccess }>({ url, method: 'PATCH', data });
  };

  transferDocumentOwner = (
    entityType: string,
    entityId: string,
    ownerUuid: string
  ): APIRequest<DocumentOwnerResponse> => {
    const url = `/entities/${entityType}/${entityId}/owner/`;
    return this.request<DocumentOwnerResponse>({ url, method: 'PATCH', data: { owner: ownerUuid } });
  };

  getOwnedDocumentCount = (userUuid: string): APIRequest<{ count: number }> => {
    const url = `/entities/documents/owned-by/${userUuid}/count/`;
    return this.request<{ count: number }>({ url, method: 'GET' });
  };

  bulkReassignDocuments = (userUuid: string, newOwnerUuid: string): APIRequest<{ reassigned: number }> => {
    const url = `/entities/documents/owned-by/${userUuid}/reassign/`;
    return this.request<{ reassigned: number }>({ url, method: 'POST', data: { owner: newOwnerUuid } });
  };

  loadFolders = (): APIRequest<FoldersResponse> => {
    const url = '/folders/';
    const method = 'GET';

    return this.request<FoldersResponse>({
      url,
      method,
    });
  };

  createFolder = (name: string, parent: string | null): APIRequest<FolderType> => {
    const url = '/folders/';
    const method = 'POST';

    return this.request<FolderType>({
      url,
      method,
      data: { name, parent },
    });
  };

  editFolder = (folderId: string, name: string, parent: string | null): APIRequest<FolderType> => {
    const url = `/folders/${folderId}/`;
    const method = 'PATCH';

    return this.request<FolderType>({
      url,
      method,
      data: { name, parent },
    });
  };

  deleteFolder = (folderId: string): APIRequest<void> => {
    const url = `/folders/${folderId}/`;
    const method = 'DELETE';

    return this.request<void>({
      url,
      method,
    });
  };

  reorderFolderContents = (
    parent: string | null,
    payload: { folders?: string[]; documents?: string[] }
  ): APIRequest<void> => {
    const url = '/folders/reorder/';
    const method = 'POST';

    return this.request<void>({
      url,
      method,
      data: { parent, ...payload },
    });
  };

  // Document Acknowledgement Requests

  listAcknowledgementRequests = (
    params?: ListAcknowledgementRequestsParams
  ): APIRequest<PaginatedResponse<AcknowledgementRequestSummary>> => {
    const url = '/entities/documents/acknowledgement-requests/';
    return this.request<PaginatedResponse<AcknowledgementRequestSummary>>({
      url,
      method: 'GET',
      params,
    });
  };

  createAcknowledgementRequest = (
    data: CreateAcknowledgementRequestBody
  ): APIRequest<AcknowledgementRequestCreateResponse> => {
    const url = '/entities/documents/acknowledgement-requests/';
    return this.request<AcknowledgementRequestCreateResponse>({
      url,
      method: 'POST',
      data,
    });
  };

  previewAcknowledgementRecipients = (data: PreviewRecipientsBody): APIRequest<PreviewRecipientsResponse> => {
    const url = '/entities/documents/acknowledgement-requests/preview-recipients/';
    return this.request<PreviewRecipientsResponse>({
      url,
      method: 'POST',
      data,
    });
  };

  previewAssignmentRuleTargetGroups = (
    data: PreviewAssignmentRuleTargetGroupsBody
  ): APIRequest<PreviewAssignmentRuleTargetGroupsResponse> => {
    const url = '/entities/events/assignment-rules/preview-target-groups/';
    return this.request<PreviewAssignmentRuleTargetGroupsResponse>({
      url,
      method: 'POST',
      data,
    });
  };

  getAcknowledgementRequest = (uuid: string): APIRequest<AcknowledgementRequestDetail> => {
    const url = `/entities/documents/acknowledgement-requests/${uuid}/`;
    return this.request<AcknowledgementRequestDetail>({
      url,
      method: 'GET',
    });
  };

  listAcknowledgements = (
    requestUuid: string,
    params?: ListAcknowledgementsParams
  ): APIRequest<PaginatedResponse<AcknowledgementRow>> => {
    const url = `/entities/documents/acknowledgement-requests/${requestUuid}/acknowledgements/`;
    return this.request<PaginatedResponse<AcknowledgementRow>>({
      url,
      method: 'GET',
      params,
    });
  };

  cancelAcknowledgementRequest = (uuid: string): APIRequest<AcknowledgementRequestDetail> => {
    const url = `/entities/documents/acknowledgement-requests/${uuid}/cancel/`;
    return this.request<AcknowledgementRequestDetail>({
      url,
      method: 'POST',
    });
  };

  acknowledgementRequestCSVUrl = (uuid: string, params: QueryParams = {}): string => {
    const query = new URLSearchParams(Object.entries(params).map(([key, value]) => [key, String(value)])).toString();
    const url = `${env.REACT_APP_API_URL}entities/documents/acknowledgement-requests/${uuid}/csv/`;
    return query ? `${url}?${query}` : url;
  };

  setDocumentTiptapVersion = (documentUuid: string, version: number): APIRequest<SetDocumentTiptapVersionResponse> => {
    const url = `/entities/documents/${documentUuid}/tiptap-version/`;
    return this.request<SetDocumentTiptapVersionResponse>({
      url,
      method: 'POST',
      data: { version },
    });
  };

  // Recipient acknowledgement flow (Phase 4)

  listMyAcknowledgements = (
    params?: ListMyAcknowledgementsParams
  ): APIRequest<PaginatedResponse<MyAcknowledgementRow>> =>
    this.request<PaginatedResponse<MyAcknowledgementRow>>({
      url: '/entities/documents/acknowledgements/',
      method: 'GET',
      params,
    });

  openAcknowledgement = (uuid: string): APIRequest<AcknowledgementOpenResponse> =>
    this.request<AcknowledgementOpenResponse>({
      url: `/entities/documents/acknowledgements/${uuid}/`,
      method: 'GET',
    });

  submitAcknowledgement = (uuid: string, data: SubmitAcknowledgementBody): APIRequest<AcknowledgementReceiptResponse> =>
    this.request<AcknowledgementReceiptResponse>({
      url: `/entities/documents/acknowledgements/${uuid}/submit/`,
      method: 'POST',
      data,
    });

  private questionHostPath = (entityType: string, host: QuestionActivityHost, hostId: string): string =>
    host === 'draft' ? `/entities/${entityType}/drafts/${hostId}/` : `/entities/${entityType}/${hostId}/submission/`;

  loadQuestionActivities = (
    entityType: string,
    host: QuestionActivityHost,
    hostId: string,
    questionKey: string,
    params?: PaginationParams
  ): APIRequest<VatixActivityListResponse> =>
    this.request<VatixActivityListResponse>({
      url: `${this.questionHostPath(entityType, host, hostId)}activities/`,
      method: 'GET',
      params: { ...params, question: questionKey },
    });

  loadQuestionActivityStats = (
    entityType: string,
    host: QuestionActivityHost,
    hostId: string
  ): APIRequest<QuestionActivityStatsResponse> =>
    this.request<QuestionActivityStatsResponse>({
      url: `${this.questionHostPath(entityType, host, hostId)}activities/stats/`,
      method: 'GET',
    });

  addQuestionComment = (
    entityType: string,
    host: QuestionActivityHost,
    hostId: string,
    message: string,
    questionKey: string,
    taggedUsers?: UserSearchResponse[]
  ): APIRequest<MessageActivityResponse> =>
    this.request<MessageActivityResponse>({
      url: `${this.questionHostPath(entityType, host, hostId)}comments/`,
      method: 'POST',
      data: { message, questionId: questionKey, taggedUsers },
    });

  uploadQuestionFile = (
    entityType: string,
    host: QuestionActivityHost,
    hostId: string,
    file: File,
    questionKey: string,
    onUploadProgress: (event: AxiosProgressEvent) => void,
    cancelTokenSource: CancelTokenSource
  ): APIRequest<ActivityResponse> => {
    const data = new FormData();
    data.append('file', file);
    data.append('questionId', questionKey);

    return this.request<ActivityResponse>({
      url: `${this.questionHostPath(entityType, host, hostId)}files/`,
      method: 'POST',
      data,
      onUploadProgress,
      cancelTokenSource,
    });
  };

  deleteQuestionComment = (
    entityType: string,
    host: QuestionActivityHost,
    hostId: string,
    messageId: string
  ): APIRequest<MessageActivityResponse> =>
    this.request<MessageActivityResponse>({
      url: `${this.questionHostPath(entityType, host, hostId)}comments/${messageId}/`,
      method: 'DELETE',
    });

  deleteQuestionFile = (
    entityType: string,
    host: QuestionActivityHost,
    hostId: string,
    fileId: string
  ): APIRequest<FileActivityResponse> =>
    this.request<FileActivityResponse>({
      url: `${this.questionHostPath(entityType, host, hostId)}files/${fileId}/`,
      method: 'DELETE',
    });

  loadQuestionLinks = (
    entityType: string,
    host: QuestionActivityHost,
    hostId: string,
    questionKey?: string,
    params?: PaginationParams
  ): APIRequest<PaginatedResponse<QuestionLink>> =>
    this.request<PaginatedResponse<QuestionLink>>({
      url: `${this.questionHostPath(entityType, host, hostId)}links/`,
      method: 'GET',
      params: { ...params, ...(questionKey ? { question: questionKey } : {}) },
    });

  loadQuestionTaskLinks = (
    entityType: string,
    host: QuestionActivityHost,
    hostId: string,
    questionKey: string,
    params?: PaginationParams
  ): APIRequest<PaginatedResponse<QuestionTaskLink>> =>
    this.request<PaginatedResponse<QuestionTaskLink>>({
      url: `${this.questionHostPath(entityType, host, hostId)}task-links/`,
      method: 'GET',
      params: { ...params, question: questionKey },
    });

  createQuestionLink = (
    entityType: string,
    host: QuestionActivityHost,
    hostId: string,
    questionKey: string,
    entityUuid: string
  ): APIRequest<QuestionLink> =>
    this.request<QuestionLink>({
      url: `${this.questionHostPath(entityType, host, hostId)}links/`,
      method: 'POST',
      data: { questionId: questionKey, entity: entityUuid },
    });

  deleteQuestionLink = (
    entityType: string,
    host: QuestionActivityHost,
    hostId: string,
    linkUuid: string
  ): APIRequest<void> =>
    this.request<void>({
      url: `${this.questionHostPath(entityType, host, hostId)}links/${linkUuid}/`,
      method: 'DELETE',
    });
}

export default EntitiesAPI;
